import { useCallback, useEffect, useRef, useState } from "react"

import useDebounce from "@hooks/useDebounce"

export interface UseSearchStateOptions {
    /** Debounce delay in milliseconds for `debouncedSearchTerm`. Defaults to 300. */
    debounce?: number
    /**
     * When this value changes (by `===` identity), the search term is cleared. Useful for
     * wiping input when a parent dialog opens/closes. Restricted to primitives so that
     * inline objects/arrays can't cause an identity change on every render.
     */
    resetOnChange?: string | number | boolean | null
}

export interface UseSearchStateReturn {
    /** The current, un-debounced input value. Bind this to the controlled input. */
    searchTerm: string
    /** `searchTerm` after the debounce window. Use this for filtering or query keys. */
    debouncedSearchTerm: string
    /** True while the debounced search term is not an empty string. */
    hasSearchTerm: boolean
    /** True while the user is actively typing and the debounce hasn't caught up yet. */
    isTyping: boolean
    /** Updates `searchTerm`. Pass to the input's `onChange`. */
    onSearchChange: (value: string) => void
    /** Clears `searchTerm` immediately. */
    resetSearch: () => void
}

export default function useSearchState(options: UseSearchStateOptions = {}): UseSearchStateReturn {
    const { debounce = 300, resetOnChange } = options

    const [searchTerm, setSearchTerm] = useState("")
    const previousResetOnChangeRef = useRef(resetOnChange)

    const debouncedSearchTerm = useDebounce(searchTerm, debounce)
    const hasSearchTerm = debouncedSearchTerm.length > 0
    const isTyping = searchTerm.length > 0 && searchTerm !== debouncedSearchTerm

    const onSearchChange = useCallback((value: string) => {
        setSearchTerm(value)
    }, [])

    const resetSearch = useCallback(() => {
        setSearchTerm("")
    }, [])

    useEffect(() => {
        if (previousResetOnChangeRef.current === resetOnChange) {
            return
        }

        previousResetOnChangeRef.current = resetOnChange
        resetSearch()
    }, [resetOnChange, resetSearch])

    return {
        searchTerm,
        debouncedSearchTerm,
        hasSearchTerm,
        isTyping,
        onSearchChange,
        resetSearch,
    }
}
